// Owns browser recording and audio mixing; editor instances supply the transient draft.
import { MEDIA_LIMITS } from '/media-policy.mjs';
import { renderFrame, blobData } from './02-render.mjs';
/** Record the bound editor draft and release recorder, audio and animation resources. */
export async function recordMedia() {
  if (!globalThis.MediaRecorder || !HTMLCanvasElement.prototype.captureStream)
    throw new Error('Video export is unavailable in this browser. Try current Safari or Chrome.');
  const mime = [
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ].find((type) => MediaRecorder.isTypeSupported(type));
  if (!mime) throw new Error('No supported video recorder.');
  const canvas = document.createElement('canvas');
  const video = this.entries[0].kind === 'video' ? document.createElement('video') : null;
  if (video) {
    video.src = this.entries[0].url;
    video.playsInline = true;
    video.muted = true;
    await new Promise((resolve, reject) => {
      video.onloadeddata = resolve;
      video.onerror = () => reject(new Error('Could not reopen the video.'));
    });
  }
  const recordingEntries = video ? [{ ...this.entries[0], source: video }] : this.entries;
  const duration = video ? video.duration : this.entries.length * this.pack.seconds;
  renderFrame(canvas, recordingEntries[0], {
    edge: 960,
    ratio: this.reel ? 9 / 16 : undefined,
    title: this.title,
  });
  const stream = canvas.captureStream(24);
  let context, frame, source, musicSource, recorder;
  try {
    if (video || this.music !== 'none') {
      context = new AudioContext();
      await context.resume();
      const dest = context.createMediaStreamDestination();
      if (video) {
        source = context.createMediaElementSource(video);
        source.connect(dest);
        video.muted = false;
      }
      if (this.music !== 'none') {
        const response = await fetch(`/assets/music/${this.music}.wav`);
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        musicSource = context.createBufferSource();
        musicSource.buffer = buffer;
        musicSource.loop = true;
        const gain = context.createGain();
        gain.gain.value = video ? 0.3 : 0.65;
        musicSource.connect(gain).connect(dest);
      }
      dest.stream.getAudioTracks().forEach((track) => stream.addTrack(track));
    }
    const chunks = [];
    recorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 1800000,
      audioBitsPerSecond: 96000,
    });
    let tooLarge = false;
    const recording = new Promise((resolve, reject) => {
      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
        if (chunks.reduce((n, c) => n + c.size, 0) > MEDIA_LIMITS.videoBytes) {
          tooLarge = true;
          if (recorder.state !== 'inactive') recorder.stop();
        }
      };
      recorder.onerror = () => reject(new Error('Video encoding failed.'));
      recorder.onstop = resolve;
    });
    if (video) {
      video.currentTime = 0;
      await video.play();
    }
    recorder.start(500);
    musicSource?.start();
    const started = performance.now();
    const draw = () => {
      const elapsed = (performance.now() - started) / 1000;
      if (this.cancelled || document.hidden) {
        if (recorder.state !== 'inactive') recorder.stop();
        return;
      }
      const index = video
        ? 0
        : Math.min(this.entries.length - 1, Math.floor(elapsed / this.pack.seconds));
      renderFrame(canvas, recordingEntries[index], {
        edge: 960,
        ratio: this.reel ? 9 / 16 : undefined,
        title: this.title,
        zoomExtra: video ? 0 : ((elapsed % this.pack.seconds) / this.pack.seconds) * 0.04,
      });
      this.status.textContent = `Preparing on this device… ${Math.min(100, Math.round((elapsed / duration) * 100))}% Keep this tab open.`;
      if (elapsed >= duration || tooLarge) {
        if (recorder.state !== 'inactive') recorder.stop();
      } else frame = requestAnimationFrame(draw);
    };
    draw();
    await recording;
    if (this.cancelled || document.hidden)
      throw new Error('Export cancelled. Keep the app visible and retry.');
    if (tooLarge) throw new Error('Video exceeds 12 MB. Use a shorter clip.');
    const type = mime.startsWith('video/mp4') ? 'video/mp4' : 'video/webm',
      blob = new Blob(chunks, { type });
    if (blob.size < 16) throw new Error('Video recording was empty.');
    this.status.textContent = `Prepared ${(blob.size / 1024 / 1024).toFixed(1)} MB video.`;
    return {
      blob,
      uploads: [
        {
          data: await blobData(blob),
          width: canvas.width,
          height: canvas.height,
          duration,
          alt: this.entries[0].alt,
        },
      ],
      title: this.title,
      music: this.music,
      musicMixed: this.music !== 'none',
      layout: 'carousel',
    };
  } finally {
    cancelAnimationFrame(frame);
    video?.pause();
    if (video) video.muted = true;
    musicSource?.stop();
    source?.disconnect();
    stream.getTracks().forEach((track) => track.stop());
    await context?.close();
  }
}
